import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    Chip,
    Divider,
    Grid,
    IconButton,
    Switch,
    Typography
} from '@mui/material';
import {
    ArrowForwardIos,
    Category,
    CheckCircle,
    DragIndicator,
    Image,
    InfoOutlined,
    Preview,
    Replay,
    Save,
    Star,
    WatchLater
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import PageHeaderLayout from '../../../components/Layout/PageHeaderLayout';
import Loading from '../../../components/Loader/Loading';
import { PhoneFrame } from './Section/MobilePreview';
import { SectionServices } from './Section/SectionServices';

const componentMeta = {
    bookAgain: {
        icon: <WatchLater sx={{ color: '#ff7043' }} />,
        bg: '#fff0ea',
        title: 'Book Again',
        subtitle: 'Recently booked services'
    },
    categories: {
        icon: <Category sx={{ color: '#2eae6d' }} />,
        bg: '#eaf8f1',
        title: 'Categories',
        subtitle: 'Browse services by category'
    },
    section: {
        icon: <Star sx={{ color: '#fb9800' }} />,
        bg: '#fff4df',
        title: 'Section',
        subtitle: 'Custom section with cards/banners'
    },
    header: {
        icon: <Image sx={{ color: '#1976d2' }} />,
        bg: '#eaf3ff',
        title: 'Header',
        subtitle: 'Top header section with banners or promotions'
    },
    footer: {
        icon: <Image sx={{ color: '#1976d2' }} />,
        bg: '#eaf3ff',
        title: 'Footer',
        subtitle: 'Bottom footer section with quick links'
    }
};

const normalizeTitle = (value) => (value || '').toString().trim().toLowerCase();

const getComponentKind = (section) => {
    const title = normalizeTitle(section?.title);
    if (title === 'book-again') return 'bookAgain';
    if (title === 'category') return 'categories';
    return 'section';
};

const getDisplayTitle = (section) => {
    const kind = getComponentKind(section);
    if (kind === 'bookAgain') return 'Book Again';
    if (kind === 'categories') return 'Categories';
    return section?.title || componentMeta.section.title;
};

const sortByPosition = (a, b) => Number(a?.position || 0) - Number(b?.position || 0);
const getPageType = (section) => section?.displayOptions?.pageType || 'section';

const HomeScreen = () => {
    const menuDispatch = useDispatch();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [header, setHeader] = useState(null);
    const [footer, setFooter] = useState(null);
    const [middleComponents, setMiddleComponents] = useState([]);
    const [previewSections, setPreviewSections] = useState([]);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    useEffect(() => {
        menuDispatch({
            type: 'menuSelect',
            payload: 'home-screen'
        });
    }, [menuDispatch]);

    const fetchByType = async (pageType) => {
        const response = await SectionServices.sectionList({
            page: 0,
            limit: 100,
            pageType,
            filters: {
                searchKey: '',
                sortBy: 'position',
                sortType: 'ASC',
                filterOptions: []
            }
        });

        return response.success == '1' ? response.data.list || [] : [];
    };

    const fetchLayout = async () => {
        try {
            setLoading(true);
            const [headers, sections, footers] = await Promise.all([
                fetchByType('header'),
                fetchByType('section'),
                fetchByType('footer')
            ]);

            setHeader(headers.filter(item => item.isActive).sort(sortByPosition)[0] || headers.sort(sortByPosition)[0] || null);
            setFooter(footers.filter(item => item.isActive).sort(sortByPosition)[0] || footers.sort(sortByPosition)[0] || null);
            setMiddleComponents(sections.filter(item => item.isActive).sort(sortByPosition));
        } catch (error) {
            Swal.fire(error.message || 'Failed to load home screen layout', '', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchPreviewSections = async () => {
        try {
            setPreviewLoading(true);
            const response = await SectionServices.homeScreenPreview();
            if (response.success == '1') {
                setPreviewSections(response.data || []);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setPreviewLoading(false);
        }
    };

    useEffect(() => {
        fetchLayout();
        fetchPreviewSections();
    }, []);

    const orderedPreviewSections = useMemo(() => {
        const previewById = new Map(previewSections.map(section => [section.id, section]));
        const headerPreview = header ? previewById.get(header.id) || header : null;
        const footerPreview = footer ? previewById.get(footer.id) || footer : null;
        const usedIds = new Set([header?.id, footer?.id].filter(Boolean));

        const orderedMiddle = middleComponents.map(section => {
            usedIds.add(section.id);
            return previewById.get(section.id) || section;
        });

        const remainingMiddle = previewSections
            .filter(section => {
                if (usedIds.has(section.id)) return false;
                if (!section.isActive) return false;
                const pageType = getPageType(section);
                return pageType !== 'header' && pageType !== 'footer';
            })
            .sort(sortByPosition);

        return [headerPreview, ...orderedMiddle, ...remainingMiddle, footerPreview]
            .filter(Boolean)
            .filter(section => section.isActive !== false);
    }, [footer, header, middleComponents, previewSections]);

    const componentTypes = useMemo(() => ([
        componentMeta.bookAgain,
        componentMeta.categories,
        componentMeta.section,
        componentMeta.footer
    ]), []);

    const handleDrop = async (dropIndex) => {
        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const next = [...middleComponents];
        const [item] = next.splice(draggedIndex, 1);
        next.splice(dropIndex, 0, item);
        setMiddleComponents(next);
        setDraggedIndex(null);
        setDragOverIndex(null);

        try {
            setLoading(true);
            const payload = next.map((section, index) => ({
                id: section.id,
                position: index + 1
            }));
            const response = await SectionServices.reorderSections(payload);
            if (response.success != '1') {
                throw new Error(response.message || 'Failed to save layout');
            }
            await fetchLayout();
            await fetchPreviewSections();
        } catch (error) {
            Swal.fire(error.message || 'Failed to save layout', '', 'error');
            await fetchLayout();
            await fetchPreviewSections();
        } finally {
            setLoading(false);
        }
    };

    const saveLayout = async () => {
        try {
            setLoading(true);
            const payload = middleComponents.map((section, index) => ({
                id: section.id,
                position: index + 1
            }));
            const response = await SectionServices.reorderSections(payload);
            if (response.success == '1') {
                await fetchPreviewSections();
                Swal.fire({ icon: 'success', title: 'Saved', text: 'Layout saved successfully', timer: 1500 });
            } else {
                Swal.fire({ icon: 'error', title: 'Failed', text: response.message || 'Failed to save layout' });
            }
        } finally {
            setLoading(false);
        }
    };

    const renderFixedComponent = (section, kind, fixedLabel) => {
        const meta = componentMeta[kind];
        return (
            <Card variant="outlined" sx={{ p: 1.5, borderRadius: 1, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <DragIndicator sx={{ color: '#9aa3af' }} />
                    <Box sx={{ width: 42, height: 42, borderRadius: 1, bgcolor: meta.bg, display: 'grid', placeItems: 'center' }}>
                        {meta.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: '0.86rem', fontWeight: 700 }}>{meta.title}</Typography>
                            <Chip size="small" label={fixedLabel} sx={{ bgcolor: '#f3e8ff', color: '#6d28d9', fontSize: '0.65rem', height: 20 }} />
                        </Box>
                        <Typography sx={{ fontSize: '0.72rem', color: '#5f6b7a' }}>
                            {section?.description || meta.subtitle}
                        </Typography>
                    </Box>
                    <Chip label={section?.isActive ? 'Active' : 'Inactive'} size="small" sx={{ bgcolor: section?.isActive ? '#e8f5e9' : '#f5f5f5', color: section?.isActive ? '#2e7d32' : '#777', fontSize: '0.65rem' }} />
                    <Button size="small" variant="outlined" sx={{ textTransform: 'none' }} onClick={() => navigate(kind === 'header' ? '/customer-app/header-management' : '/customer-app/footer-management')}>
                        Manage
                    </Button>
                    <IconButton size="small">
                        <ArrowForwardIos sx={{ fontSize: '0.8rem' }} />
                    </IconButton>
                </Box>
            </Card>
        );
    };

    return (
        <>
            {loading && <Loading />}
            <PageHeaderLayout headerTitle="Manage Home Screen Layout">
                <Button sx={{ textTransform: 'none' }} size="small" variant="outlined" startIcon={<Preview />}>
                    Preview in App
                </Button>
                <Button sx={{ textTransform: 'none' }} size="small" variant="contained" startIcon={<Save />} onClick={saveLayout}>
                    Save Layout
                </Button>
            </PageHeaderLayout>

            <Typography sx={{ color: '#667085', mb: 2 }}>
                Configure and reorder components that appear on the customer app home screen.
            </Typography>

            <Box sx={{ border: '1px solid #d8c7ff', bgcolor: '#fbf8ff', color: '#6d28d9', borderRadius: 1, p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoOutlined sx={{ fontSize: '1rem' }} />
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
                    Header and Footer are fixed in position and cannot be reordered.
                </Typography>
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={12} lg={7}>
                    <Card variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box>
                                <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>1. Layout Components</Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: '#667085' }}>
                                    Drag and drop to reorder components. Changes will reflect in the customer app.
                                </Typography>
                            </Box>
                            <Button size="small" variant="outlined" startIcon={<Replay />} sx={{ textTransform: 'none' }} onClick={() => { fetchLayout(); fetchPreviewSections(); }}>
                                Reset to Default
                            </Button>
                        </Box>

                        {renderFixedComponent(header, 'header', 'Fixed (Top)')}

                        <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden', mb: 1.5 }}>
                            {middleComponents.map((section, index) => {
                                const kind = getComponentKind(section);
                                const meta = componentMeta[kind] || componentMeta.section;
                                const isOver = dragOverIndex === index && draggedIndex !== index;
                                return (
                                    <Box
                                        key={section.id}
                                        draggable
                                        onDragStart={() => setDraggedIndex(index)}
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            setDragOverIndex(index);
                                        }}
                                        onDrop={() => handleDrop(index)}
                                        onDragEnd={() => {
                                            setDraggedIndex(null);
                                            setDragOverIndex(null);
                                        }}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            px: 1.5,
                                            py: 1.25,
                                            borderBottom: index === middleComponents.length - 1 ? 'none' : '1px solid #e5e7eb',
                                            bgcolor: isOver ? '#f8fafc' : '#fff',
                                            opacity: draggedIndex === index ? 0.55 : 1,
                                            cursor: 'grab'
                                        }}
                                    >
                                        <DragIndicator sx={{ color: '#6b7280' }} />
                                        <Typography sx={{ width: 24, fontSize: '0.8rem', color: '#475467' }}>{index + 1}</Typography>
                                        <Box sx={{ width: 42, height: 42, borderRadius: 1, bgcolor: meta.bg, display: 'grid', placeItems: 'center' }}>
                                            {meta.icon}
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontSize: '0.86rem', fontWeight: 700 }}>{getDisplayTitle(section)}</Typography>
                                            <Typography sx={{ fontSize: '0.72rem', color: '#5f6b7a' }}>
                                                {section.subtitle || section.description || meta.subtitle}
                                            </Typography>
                                        </Box>
                                        <Switch size="small" checked={!!section.isActive} inputProps={{ readOnly: true }} />
                                        <IconButton size="small">
                                            <DragIndicator sx={{ fontSize: '1rem' }} />
                                        </IconButton>
                                    </Box>
                                );
                            })}
                        </Box>

                        {renderFixedComponent(footer, 'footer', 'Fixed (Bottom)')}
                    </Card>

                    <Card variant="outlined" sx={{ p: 2, mt: 2, minHeight: 170, borderRadius: 1 }}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>2. Component Settings</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#667085', mb: 3 }}>
                            Select a component to configure its content and appearance
                        </Typography>
                        <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, minHeight: 100, display: 'grid', placeItems: 'center', textAlign: 'center', color: '#667085' }}>
                            <Box>
                                <Typography sx={{ fontSize: '0.86rem', fontWeight: 700 }}>Select a component from the list above</Typography>
                                <Typography sx={{ fontSize: '0.72rem' }}>Choose any component to edit its content, layout and settings</Typography>
                            </Box>
                        </Box>
                    </Card>
                </Grid>

                <Grid item xs={12} lg={2.4}>
                    <Card variant="outlined" sx={{ borderRadius: 1, mb: 2 }}>
                        <Typography sx={{ p: 2, fontSize: '0.9rem', fontWeight: 700 }}>Component Types</Typography>
                        <Divider />
                        {componentTypes.map((type) => (
                            <Box key={type.title} sx={{ p: 2, display: 'flex', gap: 1.5, alignItems: 'center', borderBottom: '1px solid #eef0f3' }}>
                                <Box sx={{ width: 40, height: 40, borderRadius: 1, bgcolor: type.bg, display: 'grid', placeItems: 'center' }}>{type.icon}</Box>
                                <Box>
                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{type.title}</Typography>
                                    <Typography sx={{ fontSize: '0.66rem', color: '#667085' }}>{type.subtitle}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Card>

                    <Card variant="outlined" sx={{ borderRadius: 1, bgcolor: '#f8fbff', borderColor: '#bfdbfe', p: 2 }}>
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#2563eb', mb: 1 }}>Ordering Rules</Typography>
                        {[
                            'Header is always displayed at the top',
                            'Footer is always displayed at the bottom',
                            'You can reorder items in between',
                            'Changes are saved automatically',
                            'Use Preview to see changes in app'
                        ].map(rule => (
                            <Box key={rule} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8 }}>
                                <CheckCircle sx={{ color: '#22c55e', fontSize: '0.95rem' }} />
                                <Typography sx={{ fontSize: '0.72rem', color: '#475467' }}>{rule}</Typography>
                            </Box>
                        ))}
                    </Card>
                </Grid>

                <Grid item xs={12} lg={2.6}>
                    <Card variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }}>Live Preview</Typography>
                            <IconButton size="small" onClick={fetchPreviewSections}>
                                <Replay sx={{ fontSize: '0.95rem', color: '#667085' }} />
                            </IconButton>
                        </Box>
                        <PhoneFrame sections={orderedPreviewSections} loading={previewLoading || loading} width={248} screenHeight={455} />
                    </Card>
                </Grid>
            </Grid>
        </>
    );
};

export default HomeScreen;
